import type { directory } from '@cwos/api-client';

/**
 * Turning an `app_user.id` into a person a screen can render — a name and, if
 * one exists, a photo.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS EXISTS RATHER THAN A FIELD ON EACH DOMAIN
 * ---------------------------------------------------------------------------
 * The Tasks and Org Chart payloads carry `app_user.id` and nothing else about
 * the people in them — no `assigneeName`, no `managerAvatarUrl`, no
 * `assigneePhotoUpdatedAt`. That is `docs/PROFILE-PHOTO-CONTRACT.md` holding:
 * a per-domain copy of a person's photo is a second copy of a fact that
 * changes, and copies of a changing fact go stale independently.
 *
 * So a surface resolves the id against a projection that ALREADY carries the
 * name and the photo version. `directory.workers()` is that projection: its
 * `Worker` has `userId`, `displayName` and `photoUpdatedAt` today, derived from
 * `app_user` by a correlated subquery (`WORKER_PHOTO_COL`). Nothing new is
 * invented and no domain grows an avatar field.
 *
 * ---------------------------------------------------------------------------
 * KEYED BY `userId`, NOT `worker.id`
 * ---------------------------------------------------------------------------
 * `features/listState.ts`'s `nameResolver` keys by `worker.id`, which is the
 * right key for the screens that read assignments and shifts. It is the WRONG
 * key here and would silently resolve nothing: a task's `assigneeUserId` is an
 * `app_user.id`, and the photo contract is explicit that these are not
 * interchangeable. A worker row with no linked login has no `userId` at all and
 * simply cannot appear in this map — which is correct, because a person with no
 * login is not somebody a task can be assigned to.
 *
 * ---------------------------------------------------------------------------
 * A MISSING NAME IS THREE DIFFERENT SENTENCES
 * ---------------------------------------------------------------------------
 * The same distinction `nameResolver` draws, for the same reason: "the
 * directory failed", "the directory has not answered" and "this person is
 * outside your scope" are different facts, and only the last is a statement
 * about permission. Printing the permission sentence for all three told readers
 * they were being kept from data that had merely failed to load.
 */

export interface Person {
  /** `app_user.id`. Null is legitimate and renders initials. */
  userId: string | null;
  displayName: string;
  /** ISO, or null. NULL MEANS NO PHOTO — never a loading state. */
  photoUpdatedAt: string | null;
}

export interface PersonSource {
  data: readonly directory.Worker[] | undefined;
  isLoading: boolean;
  isError: boolean;
}

/** What to show when the name cannot be resolved, and why. */
export const NAME_UNAVAILABLE = 'Name unavailable';
export const NAME_LOADING = 'Loading…';
export const NAME_OUT_OF_SCOPE = 'Someone you cannot view';
export const NOBODY = 'Nobody';

export interface PersonDirectory {
  /** The display name for an id, or the honest sentence for why there is none. */
  name(userId: string | null | undefined): string;
  /**
   * A person shape ready for `photos.personPhotoSrc`.
   *
   * When the id is unknown, `photoUpdatedAt` is null — which the contract
   * defines as "no photo, render initials". That is the only safe answer: a
   * surface that cannot carry the version cannot know whether a photo exists,
   * and guessing produces a 404 `<img>` for every colleague who has none.
   */
  person(userId: string | null | undefined): Person;
}

export function personDirectory(
  source: PersonSource,
  /** False when the reader holds neither `directory:read` nor `user:read`. */
  canReadDirectory = true,
): PersonDirectory {
  const byUserId = new Map<string, directory.Worker>();
  for (const worker of source.data ?? []) {
    if (worker.userId) byUserId.set(worker.userId, worker);
  }

  const name = (userId: string | null | undefined): string => {
    if (!userId) return NOBODY;
    const known = byUserId.get(userId);
    if (known) return known.displayName;
    if (!canReadDirectory) return NAME_OUT_OF_SCOPE;
    if (source.isError) return NAME_UNAVAILABLE;
    if (source.data === undefined) return NAME_LOADING;
    // The directory answered and this id was not in it. Scope is the honest
    // reading and the only branch entitled to say so.
    return NAME_OUT_OF_SCOPE;
  };

  return {
    name,
    person: (userId) => {
      const known = userId ? byUserId.get(userId) : undefined;
      return {
        userId: userId ?? null,
        displayName: name(userId),
        // `?? null` and never `?? something`: absent means no photo.
        photoUpdatedAt: known?.photoUpdatedAt ?? null,
      };
    },
  };
}
